import React, { useEffect, useState, useRef } from 'react';

import Spinner from '../components/Spinner';
import API from '../API';
import { getUserEmailFromToken } from '../adalConfig';

const tierOptions = [
    { value: '', label: 'All tiers' },
    { value: 'Free', label: 'Free' },
    { value: 'Starter', label: 'Starter' },
    { value: 'Standard', label: 'Standard' },
    { value: 'Business', label: 'Business' }
];

const Notifications = () => {
    const [recipients, setRecipients] = useState([]);
    const [selectedRecipients, setSelectedRecipients] = useState(new Set());
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [recipientsError, setRecipientsError] = useState('');
    const [activeOnly, setActiveOnly] = useState(true);
    const [tier, setTier] = useState('');
    const [organizationId, setOrganizationId] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);

    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [hasAction, setHasAction] = useState(false);
    const [actionLink, setActionLink] = useState('');
    const [actionText, setActionText] = useState('');
    const [extraInfo, setExtraInfo] = useState('');

    const [testMode, setTestMode] = useState(false);
    const [testEmail, setTestEmail] = useState('');

    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [sendError, setSendError] = useState('');

    // Calculate derived values
    const recipientsCount = recipients.length;
    const selectedCount = selectedRecipients.size;
    const allSelected = recipients.length > 0 && selectedRecipients.size === recipients.length;
    const someSelected = selectedRecipients.size > 0 && selectedRecipients.size < recipients.length;
    const selectAllCheckboxRef = useRef(null);

    // Load email from token when component mounts or test mode is enabled
    useEffect(() => {
        if (testMode && !testEmail) {
            const email = getUserEmailFromToken();
            if (email) {
                setTestEmail(email);
            }
        }
    }, [testMode, testEmail]);

    // Update indeterminate state of select all checkbox
    useEffect(() => {
        if (selectAllCheckboxRef.current) {
            selectAllCheckboxRef.current.indeterminate = someSelected;
        }
    }, [someSelected]);

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const fetchOrganizations = async () => {
        try {
            setLoadingOrganizations(true);
            const response = await API.getOrganizations();
            if (response && response.data) {
                setOrganizations(response.data);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
        } finally {
            setLoadingOrganizations(false);
        }
    };

    const fetchRecipients = async () => {
        setLoadingRecipients(true);
        setRecipientsError('');
        setSelectedRecipients(new Set()); // Reset selection when fetching new recipients
        try {
            const orgId = organizationId ? parseInt(organizationId, 10) : null;
            const roles = selectedRoles.length > 0 ? selectedRoles : null;
            const response = await API.getNotificationRecipients(activeOnly, tier, orgId, organizationName || null, roles);
            setRecipients(response.data);
        } catch (error) {
            setRecipientsError('Failed to fetch users for notification.');
        } finally {
            setLoadingRecipients(false);
        }
    };

    const toggleRecipientSelection = (userId) => {
        setSelectedRecipients(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    const toggleAllRecipients = () => {
        if (selectedRecipients.size === recipients.length) {
            setSelectedRecipients(new Set());
        } else {
            setSelectedRecipients(new Set(recipients.map(r => r.userId)));
        }
    };

    const handleSend = async () => {
        // Validate that recipients are selected (unless test mode)
        if (!testMode && selectedRecipients.size === 0) {
            setSendError('Please select at least one recipient.');
            return;
        }

        setSendError('');
        setSendResult(null);
        setSending(true);

        try {
            // Get selected recipients
            const recipientsToSend = testMode ? [] : recipients.filter(r => selectedRecipients.has(r.userId));

            const payload = {
                // Send specific recipient list - always send array (empty in test mode, or with selected recipients)
                recipients: recipientsToSend.map(r => ({
                    userId: r.userId,
                    email: r.email,
                    displayName: r.displayName,
                    organizationId: r.organizationId,
                    organizationName: r.organizationName,
                    subscriptionType: r.subscriptionType,
                    subscriptionStatus: r.subscriptionStatus,
                    disabled: r.disabled,
                    roles: r.roles || []
                })),
                // Filter parameters for backward compatibility (can be empty if recipients are used)
                activeOnly: testMode ? false : activeOnly,
                tier: testMode ? '' : tier,
                notificationTitle: title,
                notificationMessage: message,
                hasAction,
                actionLink: hasAction ? actionLink : '',
                actionText: hasAction ? actionText : '',
                extraInfo,
                testEmail: testMode ? testEmail : ''
            };

            // Log payload for debugging
            console.log('Sending notification payload:', {
                recipientsCount: payload.recipients.length,
                testMode: testMode,
                selectedCount: selectedRecipients.size,
                recipients: payload.recipients.map(r => ({ email: r.email, userId: r.userId }))
            });

            const response = await API.sendNotification(payload);
            setSendResult(response.data);
        } catch (error) {
            console.error(error);
            const messageFromServer = error?.response?.data?.error;
            setSendError(messageFromServer || 'Failed to send notification.');
        } finally {
            setSending(false);
        }
    };

    const roleOptions = [
        { value: 'User', label: 'User' },
        { value: 'Administrator', label: 'Administrator' },
        { value: 'Tenant Admin', label: 'Tenant Admin' }
    ];

    const toggleRole = (role) => {
        setSelectedRoles(prev => {
            if (prev.includes(role)) {
                return prev.filter(r => r !== role);
            } else {
                return [...prev, role];
            }
        });
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Notifications</h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                Send a message via Postmark to a selected user group.
                            </p>
                        </div>
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                    <input
                                        type="checkbox"
                                        checked={activeOnly}
                                        onChange={() => setActiveOnly(prev => !prev)}
                                        className="h-4 w-4 text-grafokett-blue focus:ring-grafokett-blue border-gray-300 rounded"
                                    />
                                    Show active accounts only
                                </label>
                                <select
                                    value={tier}
                                    onChange={event => setTier(event.target.value)}
                                    className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm rounded-lg focus:ring-grafokett-blue focus:border-grafokett-blue-dark px-3 py-2 text-gray-700 dark:text-gray-200"
                                >
                                    {tierOptions.map(option => (
                                        <option value={option.value} key={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Tenant ID</label>
                                    <select
                                        value={organizationId}
                                        onChange={event => setOrganizationId(event.target.value)}
                                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm rounded-lg focus:ring-grafokett-blue focus:border-grafokett-blue-dark px-3 py-2 text-gray-700 dark:text-gray-200"
                                    >
                                        <option value="">All tenants</option>
                                        {organizations.map(org => (
                                            <option value={org.id} key={org.id}>
                                                {org.id} - {org.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Tenant name</label>
                                    <input
                                        type="text"
                                        value={organizationName}
                                        onChange={event => setOrganizationName(event.target.value)}
                                        placeholder="Search by tenant name..."
                                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm rounded-lg focus:ring-grafokett-blue focus:border-grafokett-blue-dark px-3 py-2 text-gray-700 dark:text-gray-200"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Roles</label>
                                <div className="flex flex-wrap gap-3">
                                    {roleOptions.map(role => (
                                        <label key={role.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                            <input
                                                type="checkbox"
                                                checked={selectedRoles.includes(role.value)}
                                                onChange={() => toggleRole(role.value)}
                                                className="h-4 w-4 text-grafokett-blue focus:ring-grafokett-blue border-gray-300 rounded"
                                            />
                                            {role.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                <div className="flex flex-col lg:flex-row gap-6">
                    <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Selected Users</h2>
                            <div className="flex items-center gap-4">
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                    {selectedCount > 0 ? `${selectedCount} of ${recipientsCount} selected` : `${recipientsCount} recipients`}
                                </span>
                                <button
                                    onClick={fetchRecipients}
                                    disabled={loadingRecipients}
                                    className="px-4 py-2 text-sm font-medium text-white bg-grafokett-blue hover:bg-grafokett-blue-dark disabled:opacity-50 rounded-lg transition"
                                >
                                    {loadingRecipients ? 'Loading...' : 'Load Recipients'}
                                </button>
                            </div>
                        </div>

                        {loadingRecipients ? (
                            <div className="flex justify-center py-16">
                                <Spinner />
                            </div>
                        ) : recipientsError ? (
                            <div className="text-red-500 text-sm">{recipientsError}</div>
                        ) : (
                            <div className="space-y-2">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-left text-sm text-gray-600 dark:text-gray-300">
                                        <thead>
                                            <tr>
                                                <th className="pb-2 text-xs uppercase tracking-wide w-12">
                                                    <input
                                                        type="checkbox"
                                                        ref={selectAllCheckboxRef}
                                                        checked={allSelected}
                                                        onChange={toggleAllRecipients}
                                                        className="h-4 w-4 text-grafokett-blue focus:ring-grafokett-blue border-gray-300 rounded"
                                                    />
                                                </th>
                                                <th className="pb-2 text-xs uppercase tracking-wide">Name</th>
                                                <th className="pb-2 text-xs uppercase tracking-wide">Email</th>
                                                <th className="pb-2 text-xs uppercase tracking-wide">Roles</th>
                                                <th className="pb-2 text-xs uppercase tracking-wide">Tenant</th>
                                                <th className="pb-2 text-xs uppercase tracking-wide">Company</th>
                                                <th className="pb-2 text-xs uppercase tracking-wide">Tier</th>
                                                <th className="pb-2 text-xs uppercase tracking-wide">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recipients.map(recipient => (
                                                <tr key={recipient.userId} className="border-t border-gray-100 dark:border-gray-700">
                                                    <td className="py-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedRecipients.has(recipient.userId)}
                                                            onChange={() => toggleRecipientSelection(recipient.userId)}
                                                            className="h-4 w-4 text-grafokett-blue focus:ring-grafokett-blue border-gray-300 rounded"
                                                        />
                                                    </td>
                                                    <td className="py-2">{recipient.displayName || '—'}</td>
                                                    <td className="py-2">{recipient.email}</td>
                                                    <td className="py-2">
                                                        {recipient.roles && recipient.roles.length > 0 
                                                            ? recipient.roles.join(', ') 
                                                            : '—'}
                                                    </td>
                                                    <td className="py-2">{recipient.organizationId ?? '—'}</td>
                                                    <td className="py-2">{recipient.organizationName}</td>
                                                    <td className="py-2">{recipient.subscriptionType || '—'}</td>
                                                    <td className="py-2">{recipient.disabled ? 'Disabled' : recipient.subscriptionStatus || '—'}</td>
                                                </tr>
                                            ))}
                                            {recipients.length === 0 && (
                                                <tr>
                                                    <td colSpan="8" className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                                        No users match the filter.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="w-full lg:w-1/3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Send Notification</h2>
                        <div className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
                            <input
                                value={title}
                                onChange={event => setTitle(event.target.value)}
                                placeholder="Example: Cloudlabel Upgrade"
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-grafokett-blue"
                            />
                        </div>
                        <div className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Message</label>
                            <textarea
                                value={message}
                                onChange={event => setMessage(event.target.value)}
                                rows={6}
                                placeholder="Write what you want to communicate..."
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-grafokett-blue"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                id="has-action"
                                type="checkbox"
                                checked={hasAction}
                                onChange={() => setHasAction(prev => !prev)}
                                className="h-4 w-4 text-grafokett-blue focus:ring-grafokett-blue border-gray-300 rounded"
                            />
                            <label htmlFor="has-action" className="text-sm text-gray-700 dark:text-gray-300">
                                Add button/link
                            </label>
                        </div>
                        {hasAction && (
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Button text</label>
                                    <input
                                        value={actionText}
                                        onChange={event => setActionText(event.target.value)}
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-grafokett-blue"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Link</label>
                                    <input
                                        value={actionLink}
                                        onChange={event => setActionLink(event.target.value)}
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-grafokett-blue"
                                    />
                                </div>
                            </div>
                        )}
                        <div className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Extra info (optional)</label>
                            <textarea
                                value={extraInfo}
                                onChange={event => setExtraInfo(event.target.value)}
                                rows={3}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-grafokett-blue"
                            />
                        </div>
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
                            <div className="flex items-center gap-2">
                                <input
                                    id="test-mode"
                                    type="checkbox"
                                    checked={testMode}
                                    onChange={() => {
                                        setTestMode(prev => {
                                            const newValue = !prev;
                                            if (newValue && !testEmail) {
                                                const email = getUserEmailFromToken();
                                                if (email) {
                                                    setTestEmail(email);
                                                }
                                            }
                                            return newValue;
                                        });
                                    }}
                                    className="h-4 w-4 text-grafokett-blue focus:ring-grafokett-blue border-gray-300 rounded"
                                />
                                <label htmlFor="test-mode" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Send test notification
                                </label>
                            </div>
                            {testMode && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Test email address
                                    </label>
                                    <input
                                        type="email"
                                        value={testEmail}
                                        onChange={event => setTestEmail(event.target.value)}
                                        placeholder="test@example.com"
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-grafokett-blue"
                                    />
                                </div>
                            )}
                        </div>
                        {sendResult && (
                            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                                <p>Sent {sendResult.sent} of {sendResult.recipients} recipients.</p>
                                {sendResult.failed > 0 && (
                                    <p className="text-red-500">
                                        Failed for {sendResult.failed} recipients: {sendResult.failedRecipients.join(', ')}
                                    </p>
                                )}
                            </div>
                        )}
                        {sendError && <div className="text-sm text-red-500">{sendError}</div>}
                        <button
                            disabled={sending || !title || !message || (testMode && !testEmail) || (!testMode && selectedCount === 0)}
                            onClick={handleSend}
                            className="w-full inline-flex justify-center items-center px-4 py-3 text-sm font-medium text-white bg-grafokett-blue hover:bg-grafokett-blue-dark disabled:opacity-50 rounded-lg transition"
                        >
                            {sending 
                                ? 'Sending...' 
                                : testMode 
                                    ? 'Send Test Notification' 
                                    : `Send Notification${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Notifications;

